import { Link } from "react-router-dom";
import {
    Facebook,
    Instagram,
    MapPin,
    Mail,
    Phone,
    Heart,
} from "lucide-react";

const BOOTS_SRC =
    "https://customer-assets.emergentagent.com/job_faith-fashion-gift/artifacts/vjnkff7f_Untitled%20design%20%2816%29.png";

const TikTok = (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}>
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.14V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.83a8.16 8.16 0 0 0 4.77 1.52V6.9a4.85 4.85 0 0 1-1.84-.21z" />
    </svg>
);
const Pinterest = (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}>
        <path d="M12.04 2C6.5 2 4 5.65 4 8.59c0 1.77.67 3.34 2.1 3.94.24.1.45.01.52-.27.05-.18.16-.65.21-.85.07-.27.04-.36-.16-.6-.43-.51-.7-1.16-.7-2.09 0-2.7 2.02-5.12 5.27-5.12 2.87 0 4.45 1.76 4.45 4.1 0 3.08-1.36 5.68-3.39 5.68-1.12 0-1.96-.93-1.69-2.07.32-1.37.95-2.85.95-3.84 0-.89-.48-1.62-1.46-1.62-1.16 0-2.09 1.2-2.09 2.81 0 1.02.34 1.71.34 1.71s-1.17 4.95-1.38 5.84c-.41 1.74-.06 3.86-.03 4.08.02.13.18.16.26.06.11-.14 1.52-1.88 2-3.61.13-.49.77-3 .77-3 .38.72 1.49 1.36 2.67 1.36 3.51 0 5.89-3.2 5.89-7.48C18.74 4.78 16 2 12.04 2z" />
    </svg>
);

const QUICK_LINKS = [
    { label: "Shop All", to: "/shop" },
    { label: "Best Sellers", to: "/best-sellers" },
    { label: "About Us", to: "/about" },
];
const QUICK_LINKS_2 = [
    { label: "Contact", to: "/contact" },
    { label: "Shipping & Returns", to: "/contact" },
    { label: "Porch Pickup", to: "/about" },
    { label: "FAQ", to: "/contact" },
];

export default function Footer() {
    return (
        <footer className="relative" data-testid="site-footer" aria-label="Site footer">
            {/* Verse / Connect band (black with blush accents, no newsletter signup) */}
            <section
                className="bg-bb-black text-white relative overflow-hidden"
                data-testid="connect-section"
            >
                <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-bb-pink-script/10 blur-3xl pointer-events-none" />
                <div className="absolute -bottom-10 -right-10 w-48 h-48 rounded-full bg-bb-rose-gold/10 blur-3xl pointer-events-none" />

                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 md:py-16 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-10 items-center relative">
                    {/* Verse */}
                    <div className="text-center md:text-left fade-up" data-testid="verse-block">
                        <p className="font-script-alt text-bb-blush text-2xl md:text-[1.85rem] leading-snug max-w-md mx-auto md:mx-0">
                            "She is clothed in strength and dignity and she laughs without fear of the future."
                        </p>
                        <div className="mt-4 inline-flex items-center gap-3">
                            <span className="swoosh w-10" />
                            <span className="font-serif-display tracking-[0.3em] text-xs text-bb-blush">
                                PROVERBS 31:25
                            </span>
                            <span className="swoosh w-10" />
                        </div>
                    </div>

                    {/* Stay connected */}
                    <div className="text-center md:text-right fade-up">
                        <h3 className="font-serif-display text-2xl md:text-[1.65rem] tracking-[0.18em] uppercase text-bb-blush">
                            Let's Stay Connected
                        </h3>
                        <div className="swoosh w-24 mx-auto md:ml-auto md:mr-0 my-3" />
                        <p className="text-white/80 text-sm leading-relaxed max-w-sm mx-auto md:ml-auto md:mr-0">
                            Follow us on social media for new arrivals, sales, giveaways & more!
                        </p>
                        <div className="mt-5 flex items-center justify-center md:justify-end gap-3">
                            {[
                                { Icon: Facebook, label: "Facebook" },
                                { Icon: Instagram, label: "Instagram" },
                                { Icon: TikTok, label: "TikTok" },
                                { Icon: Pinterest, label: "Pinterest" },
                            ].map(({ Icon, label }, i) => (
                                <a
                                    key={label}
                                    href="#"
                                    aria-label={label}
                                    data-testid={`social-${label.toLowerCase()}`}
                                    style={{ animationDelay: `${i * 0.15}s` }}
                                    className="w-10 h-10 rounded-full bg-bb-pink-script/90 hover:bg-white text-white hover:text-bb-pink-script flex items-center justify-center transition-all duration-500 icon-hover hover:rotate-[8deg] hover:-translate-y-0.5 social-bob"
                                >
                                    <Icon size={16} />
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer body */}
            <section
                className="bg-bb-cream-2 text-bb-text relative overflow-hidden"
                data-testid="footer-main"
            >
                {/* Decorative boots PNG bottom right – tucked lower so it sits at the corner */}
                <img
                    src={BOOTS_SRC}
                    alt="Decorative cowboy boots with pink roses"
                    aria-hidden="true"
                    className="absolute -right-6 sm:-right-4 -bottom-6 sm:-bottom-4 w-40 sm:w-48 md:w-56 lg:w-64 pointer-events-none select-none gentle-float drop-shadow-[0_15px_25px_rgba(140,106,90,0.25)]"
                    draggable="false"
                />

                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:pr-56 xl:pr-64">
                    {/* Brand – text logo, no black box */}
                    <div className="fade-up">
                        <Link
                            to="/"
                            aria-label="Beyond Blessed Gifts & More LLC home"
                            className="inline-flex flex-col items-center text-center leading-none select-none group"
                            data-testid="footer-brand-logo"
                        >
                            <span className="font-serif-display tracking-[0.32em] text-bb-warm-brown-2 text-base sm:text-lg font-semibold transition-all group-hover:tracking-[0.36em]">
                                BEYOND
                            </span>
                            <span className="font-script text-bb-pink-script text-5xl sm:text-6xl -mt-1 transition-transform duration-500 group-hover:scale-105">
                                Blessed
                            </span>
                            <span className="font-serif-display tracking-[0.32em] text-bb-warm-brown-2 text-[0.65rem] sm:text-xs -mt-1">
                                GIFTS &nbsp;&amp;&nbsp; MORE&nbsp;LLC
                            </span>
                            <span className="mt-2 inline-flex items-center gap-2 text-bb-rose-gold">
                                <span className="h-px w-10 bg-bb-rose-gold/60" />
                                <Heart size={11} className="fill-current text-bb-pink-script" />
                                <span className="h-px w-10 bg-bb-rose-gold/60" />
                            </span>
                        </Link>
                        <p className="mt-5 text-sm text-bb-text-soft max-w-xs leading-relaxed">
                            A faith-based, woman-owned western boutique handpicking pieces that inspire and encourage.
                        </p>
                    </div>

                    {/* Quick links */}
                    <div className="fade-up">
                        <h4 className="font-serif-display text-base tracking-[0.25em] uppercase">
                            Quick Links
                        </h4>
                        <div className="swoosh w-12 my-3" />
                        <ul className="space-y-2 text-sm">
                            {QUICK_LINKS.map((l) => (
                                <li key={l.label}>
                                    <Link
                                        to={l.to}
                                        data-testid={`footer-link-${l.label
                                            .toLowerCase()
                                            .replace(/\s+/g, "-")}`}
                                        className="text-bb-text-soft hover:text-bb-pink-script transition-colors inline-flex items-center gap-1 hover:translate-x-1 duration-300"
                                    >
                                        <span className="opacity-0 hover:opacity-100">›</span> {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="fade-up">
                        <h4 className="font-serif-display text-base tracking-[0.25em] uppercase opacity-0 select-none">
                            .
                        </h4>
                        <div className="swoosh w-12 my-3 opacity-0" />
                        <ul className="space-y-2 text-sm">
                            {QUICK_LINKS_2.map((l) => (
                                <li key={l.label}>
                                    <Link
                                        to={l.to}
                                        data-testid={`footer-link-${l.label
                                            .toLowerCase()
                                            .replace(/[^a-z0-9]+/g, "-")}`}
                                        className="text-bb-text-soft hover:text-bb-pink-script transition-colors hover:translate-x-1 duration-300 inline-block"
                                    >
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact */}
                    <div className="fade-up">
                        <h4 className="font-serif-display text-base tracking-[0.25em] uppercase">
                            Contact Us
                        </h4>
                        <div className="swoosh w-12 my-3" />
                        <ul className="space-y-3 text-sm text-bb-text-soft">
                            <li className="flex items-center gap-3">
                                <Phone size={14} className="text-bb-pink-script" />
                                <a
                                    href="tel:+12282167117"
                                    data-testid="footer-phone"
                                    className="hover:text-bb-pink-script transition-colors"
                                >
                                    (228) 216-7117
                                </a>
                            </li>
                            <li className="flex items-center gap-3">
                                <Mail size={14} className="text-bb-pink-script shrink-0" />
                                <a
                                    href="mailto:Beyond.03.Blessed@gmail.com"
                                    data-testid="footer-email"
                                    className="hover:text-bb-pink-script transition-colors break-words leading-snug text-[0.78rem]"
                                >
                                    Beyond.03.Blessed@gmail.com
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <MapPin size={14} className="text-bb-pink-script mt-0.5" />
                                <span data-testid="footer-location" className="leading-snug">
                                    10074 Lacy Niolet Road
                                    <br />
                                    Pass Christian, MS 39571
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Copyright */}
                <div className="relative py-5 px-6 text-center text-xs text-bb-text-soft">
                    <p data-testid="footer-copyright">
                        © {new Date().getFullYear()} Beyond Blessed Gifts & More LLC. All Rights Reserved.{" "}
                        <Heart
                            size={11}
                            className="inline fill-current text-bb-pink-script animate-heart-beat"
                        />
                    </p>
                </div>
            </section>
        </footer>
    );
}
